// src/hooks/usePersistentState.js
import { useState, useEffect, useCallback } from "react";

const EVENT_NAME = "kv:persist";

function safeRead(key, initialValue) {
  if (typeof window === "undefined") return initialValue;
  try {
    const raw = window.localStorage.getItem(key);
    return raw != null ? JSON.parse(raw) : initialValue;
  } catch {
    return initialValue;
  }
}

function safeWrite(key, nextValue) {
  if (typeof window === "undefined") return;
  try {
    if (nextValue === undefined || nextValue === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(nextValue));
    }
  } catch {
    // best-effort
  }
}

export function usePersistentState(key, initialValue) {
  const [value, setValue] = useState(() => safeRead(key, initialValue));

  // 1) wrap setter: write + broadcast
  const setPersistentValue = useCallback(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;

        safeWrite(key, resolved);

        // broadcast binnen dezelfde tab
        try {
          window.dispatchEvent(
            new CustomEvent(EVENT_NAME, { detail: { key } })
          );
        } catch {
          // ignore
        }

        return resolved;
      });
    },
    [key]
  );

  // 2) luister naar:
  //    - storage (andere tabs)
  //    - custom event (zelfde tab)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onStorage = (e) => {
      if (!e || e.key !== key) return;
      setValue(safeRead(key, initialValue));
    };

    const onCustom = (e) => {
      const changedKey = e?.detail?.key;
      if (changedKey !== key) return;
      setValue(safeRead(key, initialValue));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(EVENT_NAME, onCustom);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(EVENT_NAME, onCustom);
    };
  }, [key, initialValue]);

  return [value, setPersistentValue];
}
